import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Maximize2 } from "lucide-react";

interface ScreenshotFrameProps {
  src: string;
  alt: string;
  /** Browser chrome URL label, e.g. transportmate.co.uk/dashboard */
  url?: string;
  variant?: "browser" | "device";
  className?: string;
  priority?: boolean;
}

/**
 * Presents a product screenshot inside a modern browser or device frame.
 * Click to open a larger version in a lightbox.
 */
export function ScreenshotFrame({
  src,
  alt,
  url,
  variant = "browser",
  className = "",
  priority = false,
}: ScreenshotFrameProps) {
  const [open, setOpen] = useState(false);

  const image = (
    <img
      src={src}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.015]"
    />
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Enlarge screenshot: ${alt}`}
        className={`group relative block w-full text-left rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
      >
        {variant === "browser" ? (
          <div
            className="rounded-2xl overflow-hidden border bg-card transition-shadow duration-300 group-hover:shadow-xl"
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            <div className="bg-muted/60 px-3 py-2 flex items-center gap-1.5 border-b border-border/40">
              <span className="w-2.5 h-2.5 rounded-full bg-destructive/50" />
              <span className="w-2.5 h-2.5 rounded-full bg-warning/50" />
              <span className="w-2.5 h-2.5 rounded-full bg-success/50" />
              {url && (
                <span className="ml-2 truncate text-[10px] sm:text-[11px] text-muted-foreground font-mono">
                  {url}
                </span>
              )}
            </div>
            {image}
          </div>
        ) : (
          <div
            className="mx-auto max-w-[280px] rounded-[2rem] border-4 border-foreground/85 bg-foreground/85 overflow-hidden transition-shadow duration-300 group-hover:shadow-xl"
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            <div className="relative rounded-[1.6rem] overflow-hidden bg-card">
              <span className="absolute top-1 left-1/2 -translate-x-1/2 h-1.5 w-16 rounded-full bg-foreground/70 z-10" />
              {image}
            </div>
          </div>
        )}

        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
          <Maximize2 className="h-3 w-3" aria-hidden="true" />
          View larger
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl p-2 sm:p-3">
          <img src={src} alt={alt} loading="eager" decoding="async" className="w-full h-auto rounded-lg" />
        </DialogContent>
      </Dialog>
    </>
  );
}
